import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  ClipboardDocumentListIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  StarIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  UserIcon,
  CogIcon,
  Bars3Icon,

} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { quotationsService } from '../services/logistics/quotations';
import { clientsService } from '../services/logistics/clients';
import { companySettingsService } from '../services/system/company-settings';
import { downloadQuotationPDF } from '../services/logistics/quotation-pdf-generator';
import { saudiCities } from '../data/saudiCities';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatting';

const DEFAULT_TERMS = {
  ar: [
    'هذا العرض صالح لمدة 30 يوماً من تاريخ الإصدار',
    'الأسعار شاملة ضريبة القيمة المضافة',
    'يتم الدفع خلال 30 يوماً من تاريخ الفاتورة',
    'يجب التأكد من صحة البيانات قبل الشحن'
  ],
  en: [
    'This quotation is valid for 30 days from the date of issue',
    'Prices include VAT (Value Added Tax)',
    'Payment is due within 30 days of invoice date',
    'Please verify all data before shipping'
  ]
};

const Quotations = () => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [favoriteRoutes, setFavoriteRoutes] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [languagePromptFor, setLanguagePromptFor] = useState(null);
  const [pdfSettings, setPdfSettings] = useState({
    language: 'ar',
    termsAndConditions: { ar: [...DEFAULT_TERMS.ar], en: [...DEFAULT_TERMS.en] }
  });



  // Load all data
  const loadData = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      setLoading(true);
      console.log('🔄 Loading quotations data for company:', currentCompany.id);
      
      const [quotationsData, clientsData, settingsData] = await Promise.all([
        quotationsService.getQuotations(currentCompany.id),
        clientsService.getClients(currentCompany.id),
        companySettingsService.getCompanySettings(currentCompany.id).catch(() => null)
      ]);

      setQuotations(quotationsData || []);
      setClients(clientsData || []);
      setFavoriteRoutes(
        (quotationsData || [])
          .filter(q => q.isFavorite)
          .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))
      );

      // Hydrate PDF settings from saved company settings (if present)
      const saved = settingsData?.quotationPdfSettings;
      if (saved) {
        setPdfSettings({
          language: saved.language || 'ar',
          termsAndConditions: {
            ar: Array.isArray(saved.termsAndConditions?.ar) ? saved.termsAndConditions.ar : [...DEFAULT_TERMS.ar],
            en: Array.isArray(saved.termsAndConditions?.en) ? saved.termsAndConditions.en : [...DEFAULT_TERMS.en],
          }
        });
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('Error details:', error.message);
      toast.error(t('common.error_loading_data') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter quotations
  const filteredQuotations = useMemo(() => {
    return quotations.filter(q => {
      if (q.isFavorite) return false; // Exclude favorites from main list
      
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        q.quotationNumber?.toLowerCase().includes(search) ||
        q.description?.toLowerCase().includes(search) ||
        q.customerName?.toLowerCase().includes(search) ||
        q.customerCompany?.toLowerCase().includes(search) ||
        clients.find(c => c.id === q.clientId)?.name?.toLowerCase().includes(search);

      const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
      const matchesClient = clientFilter === 'all' || q.clientId === clientFilter;
      
      return matchesSearch && matchesStatus && matchesClient;
    });
  }, [quotations, searchTerm, statusFilter, clientFilter, clients]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalQuotations = quotations.filter(q => !q.isFavorite).length;
    const totalValue = quotations.filter(q => !q.isFavorite).reduce((sum, q) => sum + (q.totalAmount || 0), 0);
    const pendingQuotations = quotations.filter(q => !q.isFavorite && q.status === 'pending').length;
    const acceptedQuotations = quotations.filter(q => !q.isFavorite && q.status === 'accepted').length;
    const conversionRate = totalQuotations > 0 ? (acceptedQuotations / totalQuotations * 100) : 0;
    
    return {
      total: totalQuotations,
      totalValue,
      pending: pendingQuotations,
      accepted: acceptedQuotations,
      conversionRate
    };
  }, [quotations]);

  // Generate the PDF client-side using @react-pdf/renderer
  const generateAndDownloadPDF = async (quotationId, language) => {
    try {
      setGeneratingPDF(true);

      let quotation = quotations.find(q => q.id === quotationId);
      if (!quotation) {
        quotation = await quotationsService.getQuotation(quotationId);
      }
      if (!quotation) throw new Error('Quotation not found');

      await downloadQuotationPDF(quotation, currentCompany, {
        language,
        termsAndConditions: pdfSettings.termsAndConditions,
        favoriteRoutes,
      });

      toast.success(language === 'ar' ? 'تم إنشاء ملف PDF بنجاح' : 'PDF generated successfully');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error((language === 'ar' ? 'فشل إنشاء ملف PDF: ' : 'Failed to generate PDF: ') + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Trigger language picker first
  const handleDownloadPDF = (quotationId) => {
    setLanguagePromptFor(quotationId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('accounting.quotations')}</h1>
          <p className="text-gray-600 mt-1">{t('accounting.quotations_description', 'Manage quotations and favorite routes')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowRouteModal(true)}
            className="btn btn-outline btn-primary"
          >
            <StarIcon className="w-4 h-4" />
            {t('accounting.manage_routes', 'Manage Routes')}
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="btn btn-outline btn-secondary"
            title="إعدادات PDF"
          >
            <CogIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="w-4 h-4" />
            {t('quotations.create_quotation')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('accounting.total_quotations')}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <DocumentTextIcon className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Favorite Routes Section - Compact Display */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <StarIconSolid className="w-5 h-5 text-yellow-500" />
              {t('accounting.favorite_routes')}
            </h3>
            <div className="flex items-center gap-2">
              <span className="badge badge-primary">{favoriteRoutes.length} {t('accounting.routes')}</span>
              <button
                onClick={() => setShowRouteModal(true)}
                className="btn btn-sm btn-outline"
              >
                <PlusIcon className="w-4 h-4" />
                {t('accounting.manage_routes')}
              </button>
            </div>
          </div>
          
          {favoriteRoutes.length === 0 ? (
            <div className="text-center py-6">
              <MapPinIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 text-sm">{t('accounting.no_favorite_routes')}</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {favoriteRoutes.map((route) => {
                const fromCity = saudiCities.find(c => c.value === route.fromCity)?.label[i18n.language] || route.fromCity;
                const toCity = saudiCities.find(c => c.value === route.toCity)?.label[i18n.language] || route.toCity;
                return (
                  <div key={route.id} className="badge badge-outline badge-lg p-3 gap-2">
                    <span className="text-xs">{fromCity} → {toCity}</span>
                    <span className="font-bold text-green-600">{formatCurrency(route.price)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('accounting.search_quotations', 'Search quotations...')}
                  className="input input-bordered w-full pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <select
              className="select select-bordered"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <option value="all">{t('common.all_clients')}</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name || client.companyName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Quotations Table */}
      <QuotationsTable
        quotations={filteredQuotations}
        clients={clients}
        loading={loading}
        generatingPDF={generatingPDF}
        onEdit={(quotation) => {
          setSelectedQuotation(quotation);
          setShowCreateModal(true);
        }}
        onDelete={async (quotation) => {
          if (window.confirm(t('accounting.confirm_delete_quotation', 'Are you sure you want to delete this quotation?'))) {
            try {
              await quotationsService.deleteQuotation(quotation.id);
              toast.success(t('accounting.quotation_deleted', 'Quotation deleted successfully'));
              loadData();
            } catch (error) {
              toast.error(t('common.error', 'Failed to delete quotation'));
            }
          }
        }}
        onDownloadPDF={async (quotation) => {
          await handleDownloadPDF(quotation.id);
        }}
        onView={(quotation) => {
          setSelectedQuotation(quotation);
          setShowViewModal(true);
        }}
      />

      {/* Modals */}
      {showCreateModal && (
        <CreateQuotationModal
          quotation={selectedQuotation}
          clients={clients}
          favoriteRoutes={favoriteRoutes}
          setFavoriteRoutes={setFavoriteRoutes}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedQuotation(null);
          }}
          onSuccess={() => {
            setShowCreateModal(false);
            setSelectedQuotation(null);
            loadData();
          }}
        />
      )}

      {showRouteModal && (
        <ManageRoutesModal
          favoriteRoutes={favoriteRoutes}
          onClose={() => setShowRouteModal(false)}
          onSuccess={loadData}
        />
      )}

      {showSettingsModal && (
        <PDFSettingsModal
          pdfSettings={pdfSettings}
          setPdfSettings={setPdfSettings}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {showViewModal && selectedQuotation && (
        <ViewQuotationModal
          quotation={selectedQuotation}
          favoriteRoutes={favoriteRoutes}
          onClose={() => {
            setShowViewModal(false);
            setSelectedQuotation(null);
          }}
        />
      )}

      {/* Language Picker Modal */}
      {languagePromptFor && (
        <LanguagePickerModal
          onCancel={() => setLanguagePromptFor(null)}
          onChoose={async (language) => {
            const id = languagePromptFor;
            setLanguagePromptFor(null);
            await generateAndDownloadPDF(id, language);
          }}
        />
      )}

      {/* PDF Generation Spinner */}
      {generatingPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-sm w-full mx-4 shadow-2xl text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 mb-4">
              <svg className="w-7 h-7 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('quotations.generating_pdf', 'Generating PDF...')}
            </h3>
          </div>
        </div>
      )}


    </div>
  );
};



// Quotations Table Component
const QuotationsTable = ({ quotations, clients, loading, generatingPDF, onEdit, onDelete, onDownloadPDF, onView }) => {
  const { t } = useTranslation();

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge badge-secondary', text: t('quotations.draft') },
      pending: { class: 'badge badge-warning', text: t('accounting.pending') },
      accepted: { class: 'badge badge-success', text: t('quotations.accepted') },
      rejected: { class: 'badge badge-error', text: t('quotations.rejected') },
      expired: { class: 'badge badge-neutral', text: t('quotations.expired') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={config.class}>{config.text}</span>;
  };

  const formatValidUntil = (value) => {
    if (!value) return '-';
    let date;
    if (typeof value?.toDate === 'function') date = value.toDate();
    else if (value?.seconds != null) date = new Date(value.seconds * 1000);
    else date = new Date(value);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  const getCompanyName = (quotation) => {
    if (quotation.clientId) {
      const client = clients.find(c => c.id === quotation.clientId);
      if (client) return client.companyName || client.name;
    }
    return quotation.customerCompany || '';
  };

  if (loading) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body p-0">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>{t('quotations.quotation_number')}</th>
                <th>{t('quotations.customer')}</th>
                <th>{t('quotations.valid_until')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((quotation) => (
                <tr key={quotation.id}>
                  <td>
                    <div className="font-medium">{quotation.quotationNumber}</div>
                    <div className="text-sm text-gray-500">{quotation.description}</div>
                  </td>
                  <td>
                    <div className="flex items-start gap-2">
                      <BuildingOfficeIcon className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      <div>
                        <div className="font-medium">
                          {getCompanyName(quotation) || <span className="text-gray-400">-</span>}
                        </div>
                        {quotation.customerName && (
                          <div className="text-xs text-gray-500">{quotation.customerName}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1 text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-gray-400" />
                      {formatValidUntil(quotation.validUntil)}
                    </div>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(quotation)}
                        className="btn btn-xs btn-ghost"
                        title={t('common.edit')}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDownloadPDF(quotation)}
                        className="btn btn-xs btn-ghost"
                        title={t('common.download')}
                        disabled={generatingPDF}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onView(quotation)}
                        className="btn btn-xs btn-info"
                        title={t('common.view', 'عرض')}
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(quotation)}
                        className="btn btn-xs btn-error"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {quotations.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">{t('quotations.no_quotations')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('accounting.no_quotations_desc', 'Create your first quotation to get started')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sortable row wrapper used for drag-and-drop reordering of routes.
// `children` is a render-prop receiving a `dragHandle` element the caller can place anywhere.
const SortableRow = ({ id, className = '', children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };
  const dragHandle = (
    <button
      type="button"
      className="cursor-grab active:cursor-grabbing touch-none p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
      {...attributes}
      {...listeners}
      title="Drag to reorder"
      aria-label="Drag to reorder"
    >
      <Bars3Icon className="w-4 h-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children(dragHandle)}
    </div>
  );
};

// City picker that shows a free-text input when "Other" is chosen.
// Custom values are stored as their typed string directly in formData,
// so getCityName() falls back to displaying them as-is.
const CityField = ({ value, onChange, placeholder, className = 'select select-bordered w-full bg-white text-sm' }) => {
  const { t, i18n } = useTranslation();
  const inList = saudiCities.some(c => c.value === value);
  const [custom, setCustom] = useState(!!value && !inList);

  // If parent resets the value (e.g. after addManualRoute), drop out of custom mode.
  useEffect(() => {
    if (!value) setCustom(false);
  }, [value]);

  if (custom) {
    return (
      <div className="flex gap-2">
        <input
          type="text"
          className="input input-bordered flex-1 bg-white text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('quotations.enter_city_name', 'Enter city name')}
          autoFocus
        />
        <button
          type="button"
          onClick={() => { setCustom(false); onChange(''); }}
          className="btn btn-sm btn-ghost"
          title={t('common.cancel')}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <select
      className={className}
      value={value}
      onChange={(e) => {
        if (e.target.value === 'other') {
          setCustom(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
    >
      <option value="">{placeholder}</option>
      {saudiCities.map(city => (
        <option key={city.value} value={city.value}>
          {city.label[i18n.language]}
        </option>
      ))}
    </select>
  );
};

// Create/Edit Quotation Modal
const CreateQuotationModal = ({ quotation, clients, favoriteRoutes, setFavoriteRoutes, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [savedClauses, setSavedClauses] = useState([]);
  const [newClause, setNewClause] = useState('');
  const [showClauseForm, setShowClauseForm] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [formData, setFormData] = useState({
    quotationNumber: '',
    customerName: '',
    customerCompany: '',
    customerPhone: '',
    customerEmail: '',
    fromCity: '',
    toCity: '',
    intermediateCities: [],
    price: '',
    description: '',
    totalAmount: '', // Total amount (calculated or manual)
    validUntil: '',
    status: 'draft',
    selectedRoutes: [],
    combinePricing: false // For PDF display only
  });

  // Utility function to convert Arabic numerals to English numerals
  const convertArabicToEnglishNumerals = (value) => {
    if (!value) return value;
    
    // Arabic-Indic digits (٠١٢٣٤٥٦٧٨٩) to English digits (0123456789)
    const arabicNumbers = '٠١٢٣٤٥٦٧٨٩';
    const englishNumbers = '0123456789';
    
    let result = value.toString();
    for (let i = 0; i < arabicNumbers.length; i++) {
      result = result.replace(new RegExp(arabicNumbers[i], 'g'), englishNumbers[i]);
    }
    
    // Also handle Persian/Farsi numerals (۰۱۲۳۴۵۶۷۸۹)
    const persianNumbers = '۰۱۲۳۴۵۶۷۸۹';
    for (let i = 0; i < persianNumbers.length; i++) {
      result = result.replace(new RegExp(persianNumbers[i], 'g'), englishNumbers[i]);
    }
    
    return result;
  };

  // Handle price input with Arabic numeral conversion
  const handlePriceChange = (e) => {
    const value = e.target.value;
    const convertedValue = convertArabicToEnglishNumerals(value);
    setFormData(prev => ({ ...prev, price: convertedValue }));
  };

  useEffect(() => {
    if (quotation) {
      setFormData({
        quotationNumber: quotation.quotationNumber || '',
        customerName: quotation.customerName || '',
        customerCompany: quotation.customerCompany || '',
        customerPhone: quotation.customerPhone || '',
        customerEmail: quotation.customerEmail || '',
        fromCity: quotation.fromCity || '',
        toCity: quotation.toCity || '',
        price: quotation.price || '',
        description: quotation.description || '',
        totalAmount: quotation.totalAmount || '',
        intermediateCities: [],
        validUntil: (() => {
          const v = quotation.validUntil;
          if (!v) return '';
          let d;
          if (typeof v?.toDate === 'function') d = v.toDate();
          else if (v?.seconds != null) d = new Date(v.seconds * 1000);
          else d = new Date(v);
          return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
        })(),
        status: quotation.status || 'draft',
        selectedRoutes: quotation.selectedRoutes || [],
        combinePricing: quotation.combinePricing !== undefined ? quotation.combinePricing : false
      });
      
      // Load manual routes from existing quotation into favoriteRoutes state
      if (quotation.manualRoutes && quotation.manualRoutes.length > 0) {
        setFavoriteRoutes(prev => {
          // Remove any existing manual routes first
          const nonManualRoutes = prev.filter(route => !route.isManual);
          // Add manual routes from quotation
          return [...nonManualRoutes, ...quotation.manualRoutes];
        });
      }
    } else {
      // Generate quotation number
      const now = new Date();
      const quotationNumber = `QUO-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
      setFormData(prev => ({ ...prev, quotationNumber }));
    }
  }, [quotation, setFavoriteRoutes]);

  // Load saved note clauses from company settings
  useEffect(() => {
    if (!currentCompany?.id) return;
    companySettingsService.getCompanySettings(currentCompany.id)
      .then(s => {
        if (Array.isArray(s?.savedNoteClauses)) setSavedClauses(s.savedNoteClauses);
      })
      .catch(() => {});
  }, [currentCompany?.id]);

  const persistClauses = async (next) => {
    if (!currentCompany?.id) return;
    try {
      await companySettingsService.updateCompanySettings(currentCompany.id, { savedNoteClauses: next });
    } catch (error) {
      console.error('Failed to persist clauses:', error);
      toast.error(t('common.error'));
    }
  };

  const handleAddClause = async () => {
    const trimmed = newClause.trim();
    if (!trimmed) return;
    const next = [...savedClauses, trimmed];
    setSavedClauses(next);
    setNewClause('');
    setShowClauseForm(false);
    await persistClauses(next);
  };

  const handleDeleteClause = async (index) => {
    const next = savedClauses.filter((_, i) => i !== index);
    setSavedClauses(next);
    await persistClauses(next);
  };

  const handleInsertClause = (clause) => {
    setFormData(prev => {
      const sep = prev.description && !prev.description.endsWith('\n') ? '\n' : '';
      return { ...prev, description: (prev.description || '') + sep + clause };
    });
  };

  // Calculate total amount based on selected routes (including manual routes)
  useEffect(() => {
    if (formData.selectedRoutes && formData.selectedRoutes.length > 0) {
      const total = favoriteRoutes
        .filter(route => formData.selectedRoutes.includes(route.id))
        .reduce((sum, route) => sum + (Number(route.price) || 0), 0);
      
      setFormData(prev => ({ ...prev, totalAmount: total }));
    } else if (formData.price) {
      // If there's a single price, use it
      setFormData(prev => ({ ...prev, totalAmount: Number(formData.price) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, totalAmount: 0 }));
    }
  }, [formData.selectedRoutes, favoriteRoutes, formData.price]);

  // Add manual route to selected routes
  const addManualRoute = async () => {
    if (!formData.fromCity || !formData.toCity || !formData.price) {
      toast.error(t('quotations.error.fill_all_fields'));
      return;
    }

    const intermediates = (formData.intermediateCities || []).filter(c => !!c);
    if (intermediates.length !== (formData.intermediateCities || []).length) {
      toast.error(t('quotations.error.fill_all_fields'));
      return;
    }

    try {
      const cities = [formData.fromCity, formData.toCity, ...intermediates];

      // Create a temporary route object for this quotation
      const manualRoute = {
        id: `manual-${Date.now()}`, // Temporary ID
        fromCity: formData.fromCity,
        toCity: formData.toCity,
        cities, // Full sequence including intermediate stops
        price: Number(formData.price),
        isManual: true // Flag to identify manual routes
      };

      // Add to favorite routes temporarily (for this session)
      setFavoriteRoutes(prev => [...prev, manualRoute]);

      // Add to selected routes
      setFormData(prev => ({
        ...prev,
        selectedRoutes: [...(prev.selectedRoutes || []), manualRoute.id],
        fromCity: '', // Clear manual entry fields
        toCity: '',
        intermediateCities: [],
        price: ''
      }));

      toast.success(t('quotations.success.manual_route_added'));
    } catch (error) {
      toast.error(t('quotations.error.add_failed'));
    }
  };

  // Handle select all functionality
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      setFormData(prev => ({ 
        ...prev, 
        selectedRoutes: favoriteRoutes.map(route => route.id)
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        selectedRoutes: []
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - require either customer name OR company name
    if (!formData.customerName && !formData.customerCompany) {
      toast.error(t('clients.customer_name_or_company_required'));
      return;
    }

    if (!currentCompany?.id) {
      toast.error(t('common.company_not_loaded', 'Company information is not available.'));
      return;
    }

    setLoading(true);
    
    try {
      // Collect manual routes data
      const manualRoutes = favoriteRoutes
        .filter(route => route.isManual && formData.selectedRoutes.includes(route.id))
        .map(route => ({
          id: route.id,
          fromCity: route.fromCity,
          toCity: route.toCity,
          cities: Array.isArray(route.cities) ? route.cities : [route.fromCity, route.toCity].filter(Boolean),
          price: route.price,
          isManual: true
        }));
      
      const quotationData = {
        ...formData,
        companyId: currentCompany.id,
        manualRoutes: manualRoutes, // Store manual routes data
        validUntil: formData.validUntil ? new Date(formData.validUntil) : null,
        createdAt: quotation ? quotation.createdAt : new Date(),
        updatedAt: new Date()
      };

      // Debug: Log what we're saving
      console.log('💾 Saving quotation:', {
        customerName: quotationData.customerName,
        customerEmail: quotationData.customerEmail,
        description: quotationData.description,
        selectedRoutes: quotationData.selectedRoutes,
        manualRoutes: manualRoutes,
        combinePricing: quotationData.combinePricing
      });

      if (quotation) {
        await quotationsService.updateQuotation(quotation.id, quotationData);
        toast.success(t('accounting.quotation_updated'));
      } else {
        await quotationsService.addQuotation(quotationData);
        toast.success(t('accounting.quotation_created'));
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving quotation:', error);
      toast.error(t('quotations.error.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  const getCityName = (cityValue) => {
    const city = saudiCities.find(c => c.value === cityValue);
    return city ? city.label.en : cityValue;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <DocumentTextIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">
                  {quotation ? t('quotations.edit_quotation') : t('quotations.create_quotation')}
                </h3>
                <p className="text-blue-100 text-sm">{t('accounting.quotation_subtitle', 'إنشاء عرض سعر احترافي للعملاء')}</p>
              </div>
            </div>
            <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost text-white hover:bg-white hover:bg-opacity-20">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Quotation Number */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="label">
              <span className="label-text font-medium">{t('quotations.quotation_number')}</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full bg-white"
              value={formData.quotationNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, quotationNumber: e.target.value }))}
              required
              readOnly
            />
          </div>

          {/* Customer Information */}
          <div className="bg-blue-50 rounded-xl p-6">
            <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-blue-600" />
              {t('accounting.customer_information', 'معلومات العميل')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('accounting.customer_company', 'Company')} <span className="text-gray-400">({t('common.optional', t('common.optional'))})</span></span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full bg-white text-sm"
                  value={formData.customerCompany}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerCompany: e.target.value }))}
                  placeholder={t('clients.customer_company_placeholder')}
                />
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('accounting.customer_name', 'Customer Name')} <span className="text-gray-400">({t('common.optional', t('common.optional'))})</span></span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full bg-white text-sm"
                  value={formData.customerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                  placeholder={t('clients.customer_name_placeholder')}
                />
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('accounting.customer_phone', 'رقم الهاتف')}</span>
                </label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  className="input input-bordered w-full bg-white"
                  placeholder={t('accounting.enter_phone', 'أدخل رقم الهاتف')}
                />
              </div>
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('accounting.customer_email', 'البريد الإلكتروني')}</span>
                </label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  className="input input-bordered w-full bg-white"
                  placeholder={t('accounting.enter_email', 'أدخل البريد الإلكتروني')}
                />
              </div>
            </div>
            
            <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {t('accounting.customer_info_note', 'Please provide either customer name or company name (both are optional, but at least one is required)')}
            </p>
          </div>

          {/* Favorite Routes */}
          {favoriteRoutes.length > 0 && (
            <div className="bg-yellow-50 rounded-xl p-4">
              <h4 className="font-semibold text-base mb-3 flex items-center gap-2">
                <StarIcon className="w-4 h-4 text-yellow-600" />
                {t('accounting.favorite_routes', 'Favorite Routes')} ({favoriteRoutes.length})
              </h4>
              <div className="flex items-center gap-2 mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                  <span className="text-sm font-medium text-yellow-700">
                    {t('common.select_all')}
                  </span>
                </label>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {favoriteRoutes.map(route => (
                  <div key={route.id} className="flex items-center justify-between bg-white rounded-lg p-2 border hover:bg-gray-50">
                    <label className="flex items-center gap-3 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={formData.selectedRoutes?.includes(route.id) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              selectedRoutes: [...(prev.selectedRoutes || []), route.id]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              selectedRoutes: prev.selectedRoutes?.filter(id => id !== route.id) || []
                            }));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">
                          {(route.cities && route.cities.length > 0
                            ? route.cities
                            : [route.fromCity, route.toCity]
                          ).map(c => getCityName(c)).join(' → ')}
                        </span>
                        <div className="text-xs text-gray-500">
                          {formatCurrency(route.price)}
                        </div>
                      </div>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(t('accounting.confirm_delete_route'))) {
                          // Remove from favorites and selected routes
                          setFavoriteRoutes(prev => prev.filter(r => r.id !== route.id));
                          setFormData(prev => ({
                            ...prev,
                            selectedRoutes: prev.selectedRoutes?.filter(id => id !== route.id) || []
                          }));
                        }
                      }}
                      className="btn btn-xs btn-ghost text-red-500 hover:bg-red-100 p-1"
                      title={t('common.delete')}
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manual Route Entry */}
          <div className="bg-green-50 rounded-xl p-4">
            <h4 className="font-semibold text-base mb-4 flex items-center gap-2">
              <MapPinIcon className="w-4 h-4 text-green-600" />
              {t('quotations.manual_route_entry')}
            </h4>

            <div className="space-y-3">
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('quotations.from_city')}</span>
                </label>
                <CityField
                  value={formData.fromCity}
                  onChange={(v) => setFormData(prev => ({ ...prev, fromCity: v }))}
                  placeholder={t('quotations.from_city')}
                />
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('quotations.to_city')}</span>
                </label>
                <CityField
                  value={formData.toCity}
                  onChange={(v) => setFormData(prev => ({ ...prev, toCity: v }))}
                  placeholder={t('quotations.to_city')}
                />
              </div>

              {(formData.intermediateCities || []).map((cityValue, idx) => (
                <div key={idx}>
                  <label className="label">
                    <span className="label-text font-medium">
                      {t('quotations.destination', 'Destination')} {idx + 1}
                    </span>
                  </label>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <CityField
                        value={cityValue}
                        onChange={(v) => setFormData(prev => {
                          const next = [...(prev.intermediateCities || [])];
                          next[idx] = v;
                          return { ...prev, intermediateCities: next };
                        })}
                        placeholder={t('quotations.select_city', 'Select city')}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        intermediateCities: (prev.intermediateCities || []).filter((_, i) => i !== idx)
                      }))}
                      className="btn btn-sm btn-ghost text-red-500 hover:bg-red-100"
                      title={t('common.remove', 'Remove')}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              <div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    intermediateCities: [...(prev.intermediateCities || []), '']
                  }))}
                  className="btn btn-sm btn-outline btn-success"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t('quotations.add_destination', 'Add destination')}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">
                    <span className="label-text font-medium">{t('quotations.trip_price')}</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered w-full bg-white text-sm"
                    value={formData.price}
                    onChange={handlePriceChange}
                    placeholder="0"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={addManualRoute}
                    className="btn btn-primary btn-sm w-full"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {t('accounting.add_route', 'Add Route')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Routes Display */}
          {(formData.selectedRoutes?.length > 0) && (() => {
            const orderedSelected = formData.selectedRoutes
              .map(id => favoriteRoutes.find(r => r.id === id))
              .filter(Boolean);
            return (
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-base mb-3 flex items-center gap-2">
                  <StarIconSolid className="w-4 h-4 text-blue-600" />
                  {t('accounting.selected_routes', 'Selected Routes')} ({formData.selectedRoutes.length})
                </h4>
                <p className="text-xs text-blue-600 mb-2">
                  {t('accounting.drag_to_reorder', 'Drag the handle to reorder routes')}
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    setFormData(prev => {
                      const ids = prev.selectedRoutes || [];
                      const oldIndex = ids.indexOf(active.id);
                      const newIndex = ids.indexOf(over.id);
                      if (oldIndex < 0 || newIndex < 0) return prev;
                      return { ...prev, selectedRoutes: arrayMove(ids, oldIndex, newIndex) };
                    });
                  }}
                >
                  <SortableContext items={orderedSelected.map(r => r.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {orderedSelected.map(route => (
                        <SortableRow
                          key={route.id}
                          id={route.id}
                          className="flex items-center gap-2 bg-white rounded-lg p-2 border"
                        >
                          {(dragHandle) => (
                            <>
                              {dragHandle}
                              <span className="text-sm font-medium flex-1">
                                {(route.cities && route.cities.length > 0
                                  ? route.cities
                                  : [route.fromCity, route.toCity]
                                ).map(c => getCityName(c)).join(' → ')}
                                {route.isManual && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{t('accounting.manual', 'Manual')}</span>}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-green-600 font-bold text-sm">
                                  {formatCurrency(route.price)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      selectedRoutes: prev.selectedRoutes.filter(id => id !== route.id)
                                    }));
                                    if (route.isManual) {
                                      setFavoriteRoutes(prev => prev.filter(r => r.id !== route.id));
                                    }
                                  }}
                                  className="btn btn-xs btn-ghost text-red-500 hover:bg-red-100 p-1"
                                  title={t('common.remove', t('common.remove'))}
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </div>
                            </>
                          )}
                        </SortableRow>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            );
          })()}

          {/* Description/Notes Field with reusable clauses */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-base flex items-center gap-2">
                <DocumentTextIcon className="w-4 h-4 text-gray-600" />
                {t('accounting.notes', 'Notes')}
              </h4>
              <button
                type="button"
                onClick={() => setShowClauseForm(v => !v)}
                className="btn btn-xs btn-outline btn-primary"
              >
                <PlusIcon className="w-3 h-3" />
                {t('quotations.save_clause', 'Save new clause')}
              </button>
            </div>

            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="textarea textarea-bordered w-full bg-white text-sm"
              rows="4"
              placeholder={t('accounting.description_placeholder', 'Add notes like: 50 SAR per stop, overnight stay charges, etc.')}
            />
            <p className="text-xs text-gray-500 mt-2">
              {t('accounting.description_help', 'Add details like stop fees, overnight charges, or special services')}
            </p>

            {/* Add new clause form */}
            {showClauseForm && (
              <div className="mt-3 p-3 bg-white border-2 border-dashed border-blue-300 rounded-lg">
                <label className="text-xs font-semibold text-blue-700 mb-1 block">
                  {t('quotations.new_clause_label', 'New reusable clause')}
                </label>
                <textarea
                  value={newClause}
                  onChange={(e) => setNewClause(e.target.value)}
                  className="textarea textarea-bordered w-full text-sm bg-white"
                  rows="2"
                  placeholder={t('quotations.clause_placeholder', 'e.g. 50 SAR charged for each additional stop')}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button type="button" onClick={() => { setShowClauseForm(false); setNewClause(''); }} className="btn btn-xs btn-ghost">
                    {t('common.cancel')}
                  </button>
                  <button type="button" onClick={handleAddClause} className="btn btn-xs btn-primary" disabled={!newClause.trim()}>
                    {t('common.save', 'Save')}
                  </button>
                </div>
              </div>
            )}

            {/* Saved clauses — click to insert */}
            {savedClauses.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  {t('quotations.saved_clauses', 'Saved clauses (click to insert)')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {savedClauses.map((clause, idx) => (
                    <div key={idx} className="group flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition">
                      <button
                        type="button"
                        onClick={() => handleInsertClause(clause)}
                        className="text-xs text-gray-700 px-3 py-1.5 hover:bg-blue-50 hover:text-blue-700 max-w-xs text-left truncate"
                        title={clause}
                      >
                        {clause}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteClause(idx)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 px-2 py-1.5 border-l border-gray-200"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pricing Options for PDF */}
          <div className="bg-yellow-50 rounded-xl p-4">
            <h4 className="font-semibold text-base mb-3 flex items-center gap-2">
              <DocumentTextIcon className="w-4 h-4 text-yellow-600" />
              {t('accounting.pdf_display_options', 'PDF Display Options')}
            </h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-sm"
                checked={formData.combinePricing}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  combinePricing: e.target.checked
                }))}
              />
              <span className="text-sm font-medium text-yellow-700">
                {t('accounting.show_total_in_pdf', 'Show total combined price in PDF')}
              </span>
            </label>
            <p className="text-xs text-yellow-600 mt-1 ml-6">
              {t('accounting.pdf_display_desc', 'When checked, PDF shows total combined price with 15% VAT. When unchecked, PDF shows individual trip prices.')}
            </p>
          </div>

          {/* Quotation Details */}
          <div className="bg-purple-50 rounded-xl p-4">
            <h4 className="font-semibold text-base mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-4 h-4 text-purple-600" />
              {t('accounting.quotation_details', 'Quotation Details')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">
                  <span className="label-text font-medium">{t('quotations.valid_until')}</span>
                </label>
                <input
                  type="date"
                  value={formData.validUntil}
                  onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                  className="input input-bordered w-full bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn btn-outline btn-lg">
              {t('common.cancel', t('common.cancel'))}
            </button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" /> : (
                <>
                  {quotation ? <PencilIcon className="w-5 h-5" /> : <PlusIcon className="w-5 h-5" />}
                  {quotation ? t('common.update', 'Update') : t('common.create', t('common.create'))}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Manage Routes Modal
const ManageRoutesModal = ({ favoriteRoutes, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newRoute, setNewRoute] = useState({ fromCity: '', toCity: '', price: '' });
  const [orderedRoutes, setOrderedRoutes] = useState(favoriteRoutes);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setOrderedRoutes(favoriteRoutes);
  }, [favoriteRoutes]);

  // Utility function to convert Arabic numerals to English numerals
  const convertArabicToEnglishNumerals = (value) => {
    if (!value) return value;

    // Arabic-Indic digits (٠١٢٣٤٥٦٧٨٩) to English digits (0123456789)
    const arabicNumbers = '٠١٢٣٤٥٦٧٨٩';
    const englishNumbers = '0123456789';

    let result = value.toString();
    for (let i = 0; i < arabicNumbers.length; i++) {
      result = result.replace(new RegExp(arabicNumbers[i], 'g'), englishNumbers[i]);
    }

    // Also handle Persian/Farsi numerals (۰۱۲۳۴۵۶۷۸۹)
    const persianNumbers = '۰۱۲۳۴۵۶۷۸۹';
    for (let i = 0; i < persianNumbers.length; i++) {
      result = result.replace(new RegExp(persianNumbers[i], 'g'), englishNumbers[i]);
    }

    return result;
  };

  // Handle price input with Arabic numeral conversion
  const handlePriceChange = (e) => {
    const value = e.target.value;
    const convertedValue = convertArabicToEnglishNumerals(value);
    setNewRoute(prev => ({ ...prev, price: convertedValue }));
  };

  const handleReorder = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedRoutes.findIndex(r => r.id === active.id);
    const newIndex = orderedRoutes.findIndex(r => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(orderedRoutes, oldIndex, newIndex);
    setOrderedRoutes(next); // optimistic
    try {
      setLoading(true);
      await Promise.all(next.map((r, idx) =>
        r.sortOrder === idx ? null : quotationsService.updateQuotation(r.id, { sortOrder: idx })
      ).filter(Boolean));
      onSuccess();
    } catch (error) {
      console.error('Error reordering routes:', error);
      toast.error(t('common.error'));
      setOrderedRoutes(favoriteRoutes); // revert
    } finally {
      setLoading(false);
    }
  };

  const handleAddRoute = async () => {
    if (!newRoute.fromCity || !newRoute.toCity || !newRoute.price) {
      toast.error(t('quotations.error.fill_all_fields'));
      return;
    }

    try {
      setLoading(true);
      const routeData = {
        companyId: currentCompany.id,
        fromCity: newRoute.fromCity,
        toCity: newRoute.toCity,
        price: Number(newRoute.price),
        isFavorite: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await quotationsService.addQuotation(routeData);
      toast.success(t('quotations.success.route_added'));
      setNewRoute({ fromCity: '', toCity: '', price: '' });
      onSuccess();
    } catch (error) {
      console.error('Error adding route:', error);
      toast.error(t('quotations.error.add_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm(t('accounting.confirm_delete_route', 'Are you sure you want to delete this route?'))) {
      return;
    }

    try {
      setLoading(true);
      await quotationsService.deleteQuotation(routeId);
      toast.success(t('quotations.success.route_removed'));
      onSuccess();
    } catch (error) {
      console.error('Error deleting route:', error);
      toast.error(t('quotations.error.remove_failed'));
    } finally {
      setLoading(false);
    }
  };

  const getCityName = (cityValue) => {
    const city = saudiCities.find(c => c.value === cityValue);
    return city ? city.label[i18n.language] : cityValue;
  };

  const [routeSearch, setRouteSearch] = useState('');

  const filteredRoutes = orderedRoutes.filter(r => {
    if (!routeSearch) return true;
    const s = routeSearch.toLowerCase();
    return (
      getCityName(r.fromCity)?.toLowerCase().includes(s) ||
      getCityName(r.toCity)?.toLowerCase().includes(s)
    );
  });
  const isReorderable = !routeSearch;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <StarIconSolid className="w-5 h-5 text-yellow-300" />
            </div>
            <div>
              <h3 className="text-lg font-bold">{t('accounting.manage_favorite_routes', 'Manage Favorite Routes')}</h3>
              <p className="text-blue-100 text-xs">{favoriteRoutes.length} {t('accounting.routes', 'routes')}</p>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost text-white hover:bg-white hover:bg-opacity-20">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {/* Add New Route - compact form */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <PlusIcon className="w-4 h-4 text-blue-600" />
              <h4 className="font-semibold text-sm text-gray-700">{t('accounting.add_new_route', 'Add New Route')}</h4>
            </div>
            <div className="grid grid-cols-12 gap-2">
              <select
                className="select select-bordered select-sm col-span-4 bg-white"
                value={newRoute.fromCity}
                onChange={(e) => setNewRoute(prev => ({ ...prev, fromCity: e.target.value }))}
              >
                <option value="">{t('quotations.from_city')}</option>
                {saudiCities.map(city => (
                  <option key={city.value} value={city.value}>{city.label[i18n.language]}</option>
                ))}
              </select>
              <select
                className="select select-bordered select-sm col-span-4 bg-white"
                value={newRoute.toCity}
                onChange={(e) => setNewRoute(prev => ({ ...prev, toCity: e.target.value }))}
              >
                <option value="">{t('quotations.to_city')}</option>
                {saudiCities.map(city => (
                  <option key={city.value} value={city.value}>{city.label[i18n.language]}</option>
                ))}
              </select>
              <input
                type="number"
                className="input input-bordered input-sm col-span-2 bg-white"
                placeholder={t('accounting.price', 'Price')}
                value={newRoute.price}
                onChange={handlePriceChange}
              />
              <button
                onClick={handleAddRoute}
                className="btn btn-primary btn-sm col-span-2"
                disabled={loading}
              >
                {loading ? <LoadingSpinner size="sm" /> : <><PlusIcon className="w-4 h-4" />{t('common.add', 'Add')}</>}
              </button>
            </div>
          </div>

          {/* Search bar */}
          {favoriteRoutes.length > 0 && (
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search', 'Search routes...')}
                className="input input-bordered input-sm w-full pl-9"
                value={routeSearch}
                onChange={(e) => setRouteSearch(e.target.value)}
              />
            </div>
          )}

          {/* Existing Routes - professional list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm text-gray-700">
                {t('accounting.existing_routes', 'Existing Routes')}
              </h4>
              <span className="text-xs text-gray-500">{filteredRoutes.length} / {favoriteRoutes.length}</span>
            </div>

            {favoriteRoutes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <MapPinIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">{t('accounting.no_favorite_routes', 'No favorite routes added yet')}</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                {/* Header row */}
                <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="col-span-1">#</div>
                  <div className="col-span-5">{t('accounting.route', 'Route')}</div>
                  <div className="col-span-4 text-right">{t('accounting.price', 'Price')}</div>
                  <div className="col-span-2 text-right">{t('common.actions')}</div>
                </div>
                {isReorderable ? (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
                    <SortableContext items={filteredRoutes.map(r => r.id)} strategy={verticalListSortingStrategy}>
                      {filteredRoutes.map((route, idx) => (
                        <SortableRow
                          key={route.id}
                          id={route.id}
                          className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors"
                        >
                          {(dragHandle) => (
                            <>
                              <div className="col-span-1 flex items-center gap-1 text-sm text-gray-500 font-medium">
                                {dragHandle}
                                <span>{idx + 1}</span>
                              </div>
                              <div className="col-span-5 flex items-center gap-2">
                                <MapPinIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                <span className="text-sm font-medium text-gray-800 truncate">
                                  {getCityName(route.fromCity)} → {getCityName(route.toCity)}
                                </span>
                              </div>
                              <div className="col-span-4 text-right">
                                <span className="text-sm font-bold text-green-600">{formatCurrency(route.price)}</span>
                              </div>
                              <div className="col-span-2 flex justify-end">
                                <button
                                  onClick={() => handleDeleteRoute(route.id)}
                                  className="btn btn-xs btn-ghost text-red-500 hover:bg-red-50"
                                  disabled={loading}
                                  title={t('common.delete')}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </>
                          )}
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  filteredRoutes.map((route, idx) => (
                    <div
                      key={route.id}
                      className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors"
                    >
                      <div className="col-span-1 text-sm text-gray-500 font-medium">{idx + 1}</div>
                      <div className="col-span-5 flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {getCityName(route.fromCity)} → {getCityName(route.toCity)}
                        </span>
                      </div>
                      <div className="col-span-4 text-right">
                        <span className="text-sm font-bold text-green-600">{formatCurrency(route.price)}</span>
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <button
                          onClick={() => handleDeleteRoute(route.id)}
                          className="btn btn-xs btn-ghost text-red-500 hover:bg-red-50"
                          disabled={loading}
                          title={t('common.delete')}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
                {filteredRoutes.length === 0 && (
                  <div className="text-center py-6 text-sm text-gray-400">
                    {t('common.no_results', 'No routes match your search')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t p-4 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="btn btn-primary">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Language Picker Modal — asks the user whether the PDF should be Arabic or English
const LanguagePickerModal = ({ onCancel, onChoose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">{t('quotations.select_pdf_language', 'Select PDF Language')}</h3>
            <p className="text-blue-100 text-xs mt-0.5">{t('quotations.select_pdf_language_desc', 'Choose the language for the quotation PDF')}</p>
          </div>
          <button onClick={onCancel} className="btn btn-sm btn-circle btn-ghost text-white hover:bg-white hover:bg-opacity-20">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <button
            onClick={() => onChoose('ar')}
            className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-500 hover:bg-blue-50 transition-all text-center group"
          >
            <div className="text-3xl mb-2">🇸🇦</div>
            <div className="font-bold text-gray-900 group-hover:text-blue-600">العربية</div>
            <div className="text-xs text-gray-500 mt-1">Arabic (RTL)</div>
          </button>
          <button
            onClick={() => onChoose('en')}
            className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-500 hover:bg-blue-50 transition-all text-center group"
          >
            <div className="text-3xl mb-2">🇬🇧</div>
            <div className="font-bold text-gray-900 group-hover:text-blue-600">English</div>
            <div className="text-xs text-gray-500 mt-1">English (LTR)</div>
          </button>
        </div>
        <div className="border-t p-4 flex justify-end">
          <button onClick={onCancel} className="btn btn-ghost btn-sm">{t('common.cancel')}</button>
        </div>
      </div>
    </div>
  );
};

// View Quotation Modal
const ViewQuotationModal = ({ quotation, favoriteRoutes, onClose }) => {
  const { t, i18n } = useTranslation();

  const getCityName = (cityValue) => {
    const city = saudiCities.find(c => c.value === cityValue);
    return city ? city.label[i18n.language] : cityValue;
  };

  const getSelectedRoutes = () => {
    if (!quotation.selectedRoutes) return [];
    return favoriteRoutes.filter(route => quotation.selectedRoutes.includes(route.id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <EyeIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">{t('common.view')} {t('accounting.quotation')}</h3>
                <p className="text-green-100 text-sm">{quotation.quotationNumber}</p>
              </div>
            </div>
            <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost text-white hover:bg-white hover:bg-opacity-20">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Information */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-blue-600" />
              {t('accounting.customer_information')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><strong>{t('accounting.customer_name')}:</strong> {quotation.customerName || '-'}</div>
              <div><strong>{t('accounting.customer_company')}:</strong> {quotation.customerCompany || '-'}</div>
              <div><strong>{t('accounting.customer_phone')}:</strong> {quotation.customerPhone || '-'}</div>
              <div><strong>{t('accounting.customer_email')}:</strong> {quotation.customerEmail || '-'}</div>
            </div>
          </div>

          {/* Route Information */}
          {(quotation.fromCity || quotation.toCity) && (
            <div className="bg-green-50 rounded-xl p-4">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <MapPinIcon className="w-5 h-5 text-green-600" />
                {t('accounting.route_information')}
              </h4>
              <div className="text-sm">
                <div><strong>{t('quotations.from_city')}:</strong> {getCityName(quotation.fromCity) || '-'}</div>
                <div><strong>{t('quotations.to_city')}:</strong> {getCityName(quotation.toCity) || '-'}</div>
              </div>
            </div>
          )}

          {/* Selected Routes */}
          {getSelectedRoutes().length > 0 && (
            <div className="bg-yellow-50 rounded-xl p-4">
              <h4 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <StarIconSolid className="w-5 h-5 text-yellow-600" />
                {t('accounting.selected_routes')}
              </h4>
              <div className="space-y-2">
                {getSelectedRoutes().map(route => (
                  <div key={route.id} className="flex items-center justify-between bg-white rounded-lg p-2 border">
                    <span className="text-sm font-medium">
                      {getCityName(route.fromCity)} → {getCityName(route.toCity)}
                    </span>
                    <span className="text-green-600 font-bold text-sm">
                      {formatCurrency(route.price)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quotation Details */}
          <div className="bg-purple-50 rounded-xl p-4">
            <h4 className="font-semibold text-base mb-4 flex items-center gap-2">
              <DocumentTextIcon className="w-4 h-4 text-purple-600" />
              {t('accounting.quotation_details')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><strong>{t('quotations.valid_until')}:</strong> {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString() : '-'}</div>
              <div><strong>{t('common.created')}:</strong> {new Date(quotation.createdAt).toLocaleDateString()}</div>
            </div>
            {quotation.description && (
              <div className="mt-3">
                <strong>{t('common.description')}:</strong>
                <p className="mt-1 text-gray-600">{quotation.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 p-6 border-t">
          <button onClick={onClose} className="btn btn-outline">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};



// PDF Settings Modal Component
const PDFSettingsModal = ({ pdfSettings, setPdfSettings, onClose }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    language: pdfSettings.language || 'ar',
    termsAndConditions: {
      ar: [...(pdfSettings.termsAndConditions.ar || [])],
      en: [...(pdfSettings.termsAndConditions.en || [])]
    }
  });

  const handleAddTerm = (language) => {
    setLocalSettings(prev => ({
      ...prev,
      termsAndConditions: {
        ...prev.termsAndConditions,
        [language]: [...prev.termsAndConditions[language], '']
      }
    }));
  };

  const handleUpdateTerm = (language, index, value) => {
    setLocalSettings(prev => ({
      ...prev,
      termsAndConditions: {
        ...prev.termsAndConditions,
        [language]: prev.termsAndConditions[language].map((term, i) => 
          i === index ? value : term
        )
      }
    }));
  };

  const handleDeleteTerm = (language, index) => {
    setLocalSettings(prev => ({
      ...prev,
      termsAndConditions: {
        ...prev.termsAndConditions,
        [language]: prev.termsAndConditions[language].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = async () => {
    if (!currentCompany?.id) {
      toast.error(t('common.error'));
      return;
    }
    try {
      setSaving(true);
      // Filter out empty terms before persisting
      const cleaned = {
        language: localSettings.language,
        termsAndConditions: {
          ar: (localSettings.termsAndConditions.ar || []).filter(t => t && t.trim()),
          en: (localSettings.termsAndConditions.en || []).filter(t => t && t.trim()),
        }
      };
      await companySettingsService.updateCompanySettings(currentCompany.id, {
        quotationPdfSettings: cleaned,
      });
      setPdfSettings(cleaned);
      toast.success(t('common.saved', 'Settings saved successfully'));
      onClose();
    } catch (error) {
      console.error('Failed to save PDF settings:', error);
      toast.error(t('common.error') + ': ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <CogIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">إعدادات PDF</h3>
                <p className="text-blue-100 text-sm">تخصيص إعدادات ملف PDF وتحديد اللغة</p>
              </div>
            </div>
            <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost text-white hover:bg-white hover:bg-opacity-20">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Language Selection */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.5 0h-3M9 21v-6m0 0H4.5M9 15h10.5M4.5 15H3m6 6h12" />
              </svg>
              {t('accounting.pdf_language', 'PDF Language')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-transparent hover:border-blue-200 cursor-pointer transition-all">
                <input
                  type="radio"
                  className="radio radio-primary"
                  checked={localSettings.language === 'ar'}
                  onChange={() => setLocalSettings(prev => ({ ...prev, language: 'ar' }))}
                />
                <div>
                  <span className="font-medium text-gray-900">{t('accounting.pdf_language_ar', 'العربية')} (Arabic)</span>
                  <p className="text-xs text-gray-500">{t('accounting.rtl_layout', 'RTL Layout')}</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-transparent hover:border-blue-200 cursor-pointer transition-all">
                <input
                  type="radio"
                  className="radio radio-primary"
                  checked={localSettings.language === 'en'}
                  onChange={() => setLocalSettings(prev => ({ ...prev, language: 'en' }))}
                />
                <div>
                  <span className="font-medium text-gray-900">{t('accounting.pdf_language_en', 'English')}</span>
                  <p className="text-xs text-gray-500">{t('accounting.ltr_layout', 'LTR Layout')}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Terms and Conditions - Arabic */}
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                الشروط والأحكام (العربية)
              </h4>
              <button
                onClick={() => handleAddTerm('ar')}
                className="btn btn-sm btn-primary"
              >
                <PlusIcon className="w-4 h-4" />
                إضافة شرط
              </button>
            </div>
            
            <div className="space-y-3">
              {localSettings.termsAndConditions.ar.map((term, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-1">
                    <textarea
                      className="textarea textarea-bordered w-full text-sm bg-white"
                      value={term}
                      onChange={(e) => handleUpdateTerm('ar', index, e.target.value)}
                      placeholder="أدخل نص الشرط..."
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={() => handleDeleteTerm('ar', index)}
                    className="btn btn-sm btn-error btn-outline"
                    title="حذف"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Terms and Conditions - English */}
          <div className="bg-purple-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-lg flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Terms and Conditions (English)
              </h4>
              <button
                onClick={() => handleAddTerm('en')}
                className="btn btn-sm btn-primary"
              >
                <PlusIcon className="w-4 h-4" />
                Add Term
              </button>
            </div>
            
            <div className="space-y-3">
              {localSettings.termsAndConditions.en.map((term, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-1">
                    <textarea
                      className="textarea textarea-bordered w-full text-sm bg-white"
                      value={term}
                      onChange={(e) => handleUpdateTerm('en', index, e.target.value)}
                      placeholder="Enter term text..."
                      rows="2"
                    />
                  </div>
                  <button
                    onClick={() => handleDeleteTerm('en', index)}
                    className="btn btn-sm btn-error btn-outline"
                    title={t('common.delete')}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button onClick={onClose} className="btn btn-outline btn-lg">
              إلغاء
            </button>
            <button onClick={handleSave} className="btn btn-primary btn-lg" disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : 'حفظ الإعدادات'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quotations;